import React, { useState, useEffect, useCallback, useRef } from 'react';
import Navbar from '../components/Navbar';
import api, { extractErrorMessage } from '../services/api';
import { useToast } from '../components/Toast';
import '../styles/Media.css';

function Media({ user }) {
  const toast = useToast();
  const [media, setMedia] = useState([]);
  const [collections, setCollections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('media');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('media');
  const [editItem, setEditItem] = useState(null);
  const [filters, setFilters] = useState({ category: '', type: '', search: '' });

  const fetchData = useCallback(async () => {
    try {
      const [mediaRes, collectionsRes, categoriesRes, statsRes] = await Promise.all([
        api.get('/api/media', { params: filters }),
        api.get('/api/media/collections/all'),
        api.get('/api/media/categories/all'),
        api.get('/api/media/stats/overview')
      ]);

      setMedia(mediaRes.data.media || []);
      setCollections(collectionsRes.data || []);
      setCategories(categoriesRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching media data:', error);
      toast.error(extractErrorMessage(error, 'Medien-Daten konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (type, id) => {
    if (!window.confirm('Wirklich löschen?')) return;

    try {
      if (type === 'media') {
        await api.delete(`/api/media/${id}`);
      } else if (type === 'collection') {
        await api.delete(`/api/media/collections/${id}`);
      } else if (type === 'category') {
        await api.delete(`/api/media/categories/${id}`);
      }
      toast.success('Erfolgreich gelöscht');
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen'));
    }
  };

  const handleToggleActive = async (type, item) => {
    try {
      if (type === 'media') {
        await api.put(`/api/media/${item.id}`, { active: !item.active });
      } else if (type === 'collection') {
        await api.put(`/api/media/collections/${item.id}`, { active: !item.active });
      }
      fetchData();
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditItem(item);
    setShowModal(true);
  };

  const getTypeIcon = (type) => {
    const icons = {
      'image': '🖼️',
      'video': '🎬',
      'audio': '🎵',
      'gallery': '📸',
      'document': '📄'
    };
    return icons[type] || '📁';
  };

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_URL || 'http://localhost:3004';
  };

  if (loading) {
    return (
      <div className="app-layout">
        <Navbar user={user} />
        <main className="main-content">
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="media-page">
          <header className="page-header">
            <h1>🎁 Media Verwaltung</h1>
            <p>Verwalte Medien, Collections und Kategorien für das Belohnungssystem</p>
          </header>

          {/* Stats Overview */}
          {stats && (
            <div className="stats-grid">
              <div className="stat-card">
                <span className="stat-icon">🖼️</span>
                <div className="stat-info">
                  <span className="stat-value">{stats.total_media}</span>
                  <span className="stat-label">Medien</span>
                </div>
              </div>
              <div className="stat-card">
                <span className="stat-icon">📦</span>
                <div className="stat-info">
                  <span className="stat-value">{stats.total_collections}</span>
                  <span className="stat-label">Collections</span>
                </div>
              </div>
              <div className="stat-card">
                <span className="stat-icon">🔓</span>
                <div className="stat-info">
                  <span className="stat-value">{stats.total_unlocks}</span>
                  <span className="stat-label">Freischaltungen</span>
                </div>
              </div>
              <div className="stat-card">
                <span className="stat-icon">👁️</span>
                <div className="stat-info">
                  <span className="stat-value">{stats.total_views}</span>
                  <span className="stat-label">Aufrufe</span>
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'media' ? 'active' : ''}`}
              onClick={() => setActiveTab('media')}
            >
              🖼️ Medien ({media.length})
            </button>
            <button
              className={`tab ${activeTab === 'collections' ? 'active' : ''}`}
              onClick={() => setActiveTab('collections')}
            >
              📦 Collections ({collections.length})
            </button>
            <button
              className={`tab ${activeTab === 'categories' ? 'active' : ''}`}
              onClick={() => setActiveTab('categories')}
            >
              📁 Kategorien ({categories.length})
            </button>
          </div>

          {/* Media Tab */}
          {activeTab === 'media' && (
            <div className="tab-content">
              <div className="toolbar">
                <div className="filters">
                  <input
                    type="text"
                    placeholder="Suchen..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  />
                  <select
                    value={filters.type}
                    onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                  >
                    <option value="">Alle Typen</option>
                    <option value="image">Bilder</option>
                    <option value="video">Videos</option>
                    <option value="audio">Audio</option>
                    <option value="gallery">Galerie</option>
                    <option value="document">Dokument</option>
                  </select>
                  <select
                    value={filters.category}
                    onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  >
                    <option value="">Alle Kategorien</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.display_name}</option>
                    ))}
                  </select>
                </div>
                <button className="btn btn-primary" onClick={() => openModal('media')}>
                  + Neues Media
                </button>
              </div>

              <div className="media-grid">
                {media.map(item => (
                  <div key={item.id} className={`media-card ${!item.active ? 'inactive' : ''}`}>
                    {item.thumbnail_url || (item.type === 'image' && item.url) ? (
                      <div
                        className="media-preview"
                        style={{
                          backgroundImage: `url(${getApiBaseUrl()}${item.thumbnail_url || item.url})`
                        }}
                      >
                        {item.is_nsfw && <span className="badge nsfw">🔞</span>}
                        {item.is_premium && <span className="badge premium">⭐</span>}
                      </div>
                    ) : (
                      <div className="media-preview placeholder">
                        <span className="type-icon">{getTypeIcon(item.type)}</span>
                        {item.is_nsfw && <span className="badge nsfw">🔞</span>}
                        {item.is_premium && <span className="badge premium">⭐</span>}
                      </div>
                    )}
                    <div className="media-info">
                      <h4>{item.title}</h4>
                      <div className="media-meta">
                        <span>{getTypeIcon(item.type)} {item.type}</span>
                        <span>💰 {item.points_cost} Pkt</span>
                      </div>
                      <div className="media-stats">
                        <span>🔓 {item.unlock_count}</span>
                        <span>👁️ {item.view_count}</span>
                      </div>
                    </div>
                    <div className="media-actions">
                      <button onClick={() => openModal('media', item)} title="Bearbeiten">✏️</button>
                      <button onClick={() => handleToggleActive('media', item)} title={item.active ? 'Deaktivieren' : 'Aktivieren'}>
                        {item.active ? '🟢' : '🔴'}
                      </button>
                      <button onClick={() => handleDelete('media', item.id)} title="Löschen">🗑️</button>
                    </div>
                  </div>
                ))}
              </div>

              {media.length === 0 && (
                <div className="empty-state">
                  <span>🖼️</span>
                  <p>Noch keine Medien vorhanden</p>
                  <button className="btn btn-primary" onClick={() => openModal('media')}>
                    Erstes Media erstellen
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Collections Tab */}
          {activeTab === 'collections' && (
            <div className="tab-content">
              <div className="toolbar">
                <div className="filters"></div>
                <button className="btn btn-primary" onClick={() => openModal('collection')}>
                  + Neue Collection
                </button>
              </div>

              <div className="collections-list">
                {collections.map(coll => (
                  <div key={coll.id} className={`collection-card ${!coll.active ? 'inactive' : ''}`}>
                    {coll.cover_image && (
                      <div
                        className="collection-cover"
                        style={{ backgroundImage: `url(${getApiBaseUrl()}${coll.cover_image})` }}
                      ></div>
                    )}
                    <div className="collection-info">
                      <h4>
                        {coll.name}
                        {coll.is_premium && <span className="badge premium">⭐</span>}
                      </h4>
                      <p>{coll.description || 'Keine Beschreibung'}</p>
                      <div className="collection-meta">
                        <span>📊 {coll.item_count} Medien</span>
                        <span>💰 {coll.points_cost} Punkte</span>
                        {coll.discount_percent > 0 && (
                          <span className="discount">-{coll.discount_percent}%</span>
                        )}
                      </div>
                    </div>
                    <div className="collection-actions">
                      <button onClick={() => openModal('collection', coll)}>✏️</button>
                      <button onClick={() => handleToggleActive('collection', coll)}>
                        {coll.active ? '🟢' : '🔴'}
                      </button>
                      <button onClick={() => handleDelete('collection', coll.id)}>🗑️</button>
                    </div>
                  </div>
                ))}
              </div>

              {collections.length === 0 && (
                <div className="empty-state">
                  <span>📦</span>
                  <p>Noch keine Collections vorhanden</p>
                  <button className="btn btn-primary" onClick={() => openModal('collection')}>
                    Erste Collection erstellen
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Categories Tab */}
          {activeTab === 'categories' && (
            <div className="tab-content">
              <div className="toolbar">
                <div className="filters"></div>
                <button className="btn btn-primary" onClick={() => openModal('category')}>
                  + Neue Kategorie
                </button>
              </div>

              <div className="categories-list">
                {categories.map(cat => (
                  <div key={cat.id} className="category-card">
                    <div className="category-icon" style={{ backgroundColor: cat.color }}>
                      {cat.icon || '📁'}
                    </div>
                    <div className="category-info">
                      <h4>{cat.display_name}</h4>
                      <p>{cat.description || 'Keine Beschreibung'}</p>
                      <span className="category-meta">{cat.media_count || 0} Medien</span>
                    </div>
                    <div className="category-actions">
                      <button onClick={() => openModal('category', cat)}>✏️</button>
                      <button onClick={() => handleDelete('category', cat.id)}>🗑️</button>
                    </div>
                  </div>
                ))}
              </div>

              {categories.length === 0 && (
                <div className="empty-state">
                  <span>📁</span>
                  <p>Noch keine Kategorien vorhanden</p>
                  <button className="btn btn-primary" onClick={() => openModal('category')}>
                    Erste Kategorie erstellen
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <MediaModal
            type={modalType}
            item={editItem}
            categories={categories}
            media={media}
            onClose={() => {
              setShowModal(false);
              setEditItem(null);
            }}
            onSave={() => {
              setShowModal(false);
              setEditItem(null);
              fetchData();
            }}
          />
        )}
      </main>
    </div>
  );
}

function MediaModal({ type, item, categories, media, onClose, onSave }) {
  const toast = useToast();
  const [formData, setFormData] = useState(
    item || getDefaultFormData(type)
  );
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);

  useEffect(() => {
    if (type === 'collection' && item) {
      // Load collection items
      api.get(`/api/media/collections/${item.id}`)
        .then(res => {
          setSelectedMedia(res.data.items?.map(m => m.id) || []);
        })
        .catch(console.error);
    }
  }, [type, item]);

  const handleFileUpload = async (file, isThumb = false) => {
    if (!file) return null;

    setUploading(true);
    setUploadProgress(0);

    const uploadFormData = new FormData();
    if (isThumb) {
      uploadFormData.append('thumbnail', file);
    } else {
      uploadFormData.append('file', file);
    }

    try {
      const response = await api.post('/api/media/upload', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      setUploading(false);
      return response.data;
    } catch (error) {
      console.error('Upload error:', error);
      setUploading(false);
      toast.error(extractErrorMessage(error, 'Fehler beim Hochladen'));
      return null;
    }
  };

  const handleMainFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const result = await handleFileUpload(file);
    if (result) {
      setFormData(prev => ({
        ...prev,
        url: result.url,
        type: result.type,
        file_size: result.file_size
      }));
    }
  };

  const handleThumbnailSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const result = await handleFileUpload(file, true);
    if (result) {
      setFormData(prev => ({
        ...prev,
        thumbnail_url: result.thumbnail_url || result.url
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);

    try {
      let endpoint, method;
      const submitData = { ...formData };

      if (type === 'media') {
        endpoint = item ? `/api/media/${item.id}` : '/api/media';
        method = item ? 'put' : 'post';
      } else if (type === 'collection') {
        endpoint = item ? `/api/media/collections/${item.id}` : '/api/media/collections';
        method = item ? 'put' : 'post';
        submitData.media_ids = selectedMedia;
      } else if (type === 'category') {
        endpoint = item ? `/api/media/categories/${item.id}` : '/api/media/categories';
        method = item ? 'put' : 'post';
      }

      await api[method](endpoint, submitData);
      toast.success('Erfolgreich gespeichert');
      onSave();
    } catch (error) {
      console.error('Error saving:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Speichern'));
    } finally {
      setSaving(false);
    }
  };

  const toggleMediaSelection = (mediaId) => {
    setSelectedMedia(prev =>
      prev.includes(mediaId)
        ? prev.filter(id => id !== mediaId)
        : [...prev, mediaId]
    );
  };

  const closeModal = () => {
    if (saving || uploading) return;
    onClose();
  };

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_URL || 'http://localhost:3004';
  };

  return (
    <div className="modal-overlay" onClick={closeModal}>
      <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <header className="modal-header">
          <h3>
            {item ? 'Bearbeiten' : 'Erstellen'}: {
              type === 'media' ? 'Media' :
              type === 'collection' ? 'Collection' : 'Kategorie'
            }
          </h3>
          <button className="close-btn" onClick={closeModal}>&times;</button>
        </header>

        {formError && <div className="shared-form-error-banner">{formError}</div>}
        <form onSubmit={handleSubmit}>
          {type === 'media' && (
            <>
              <div className="form-group">
                <label>Titel *</label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Beschreibung</label>
                <textarea
                  value={formData.description || ''}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              {/* File Upload Section */}
              <div className="form-group upload-section">
                <label>Datei hochladen</label>
                <div className="upload-area">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleMainFileSelect}
                    accept="image/*,video/*,audio/*,.pdf"
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    📤 Datei auswählen
                  </button>
                  {uploading && (
                    <div className="upload-progress">
                      <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                      <span>{uploadProgress}%</span>
                    </div>
                  )}
                  {formData.url && (
                    <div className="file-preview">
                      ✅ {formData.url.startsWith('/uploads/') ? 'Hochgeladen' : 'URL gesetzt'}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Typ</label>
                  <select
                    value={formData.type || 'image'}
                    onChange={e => setFormData({ ...formData, type: e.target.value })}
                  >
                    <option value="image">🖼️ Bild</option>
                    <option value="video">🎬 Video</option>
                    <option value="audio">🎵 Audio</option>
                    <option value="gallery">📸 Galerie</option>
                    <option value="document">📄 Dokument</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Kategorie</label>
                  <select
                    value={formData.category || 'general'}
                    onChange={e => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="general">Allgemein</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.display_name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>URL (oder manuelle Eingabe)</label>
                <input
                  type="text"
                  value={formData.url || ''}
                  onChange={e => setFormData({ ...formData, url: e.target.value })}
                  placeholder="/uploads/... oder https://..."
                  required
                />
              </div>

              {/* Thumbnail Upload */}
              <div className="form-group upload-section">
                <label>Thumbnail (optional)</label>
                <div className="upload-area">
                  <input
                    type="file"
                    ref={thumbnailInputRef}
                    onChange={handleThumbnailSelect}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary upload-btn"
                    onClick={() => thumbnailInputRef.current?.click()}
                    disabled={uploading}
                  >
                    🖼️ Thumbnail auswählen
                  </button>
                  {formData.thumbnail_url && (
                    <div className="thumbnail-preview">
                      <img
                        src={`${getApiBaseUrl()}${formData.thumbnail_url}`}
                        alt="Thumbnail"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Thumbnail URL (oder manuelle Eingabe)</label>
                <input
                  type="text"
                  value={formData.thumbnail_url || ''}
                  onChange={e => setFormData({ ...formData, thumbnail_url: e.target.value })}
                  placeholder="/uploads/thumbnails/... oder https://..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Punktekosten</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.points_cost || 0}
                    onChange={e => setFormData({ ...formData, points_cost: parseInt(e.target.value) })}
                  />
                </div>
                <div className="form-group">
                  <label>Tags (kommagetrennt)</label>
                  <input
                    type="text"
                    value={formData.tags || ''}
                    onChange={e => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="tag1, tag2, tag3"
                  />
                </div>
              </div>
              <div className="form-row checkboxes">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_nsfw || false}
                    onChange={e => setFormData({ ...formData, is_nsfw: e.target.checked })}
                  />
                  🔞 NSFW
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_premium || false}
                    onChange={e => setFormData({ ...formData, is_premium: e.target.checked })}
                  />
                  ⭐ Premium
                </label>
              </div>
            </>
          )}

          {type === 'collection' && (
            <>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Beschreibung</label>
                <textarea
                  value={formData.description || ''}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Cover-Bild URL</label>
                <input
                  type="text"
                  value={formData.cover_image || ''}
                  onChange={e => setFormData({ ...formData, cover_image: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Punktekosten</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.points_cost || 0}
                    onChange={e => setFormData({ ...formData, points_cost: parseInt(e.target.value) })}
                  />
                </div>
                <div className="form-group">
                  <label>Rabatt %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.discount_percent || 0}
                    onChange={e => setFormData({ ...formData, discount_percent: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_premium || false}
                    onChange={e => setFormData({ ...formData, is_premium: e.target.checked })}
                  />
                  ⭐ Premium Collection
                </label>
              </div>
              <div className="form-group">
                <label>Medien auswählen ({selectedMedia.length} ausgewählt)</label>
                <div className="media-selector">
                  {media.map(m => (
                    <div
                      key={m.id}
                      className={`media-select-item ${selectedMedia.includes(m.id) ? 'selected' : ''}`}
                      onClick={() => toggleMediaSelection(m.id)}
                    >
                      <span className="check">{selectedMedia.includes(m.id) ? '✓' : ''}</span>
                      <span>{m.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {type === 'category' && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Name (intern) *</label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={e => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    required
                    disabled={!!item}
                  />
                </div>
                <div className="form-group">
                  <label>Anzeigename *</label>
                  <input
                    type="text"
                    value={formData.display_name || ''}
                    onChange={e => setFormData({ ...formData, display_name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Beschreibung</label>
                <textarea
                  value={formData.description || ''}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Icon (Emoji)</label>
                  <input
                    type="text"
                    value={formData.icon || ''}
                    onChange={e => setFormData({ ...formData, icon: e.target.value })}
                    placeholder="📁"
                    maxLength={4}
                  />
                </div>
                <div className="form-group">
                  <label>Farbe</label>
                  <input
                    type="color"
                    value={formData.color || '#5865F2'}
                    onChange={e => setFormData({ ...formData, color: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Sortierung</label>
                  <input
                    type="number"
                    value={formData.sort_order || 0}
                    onChange={e => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                  />
                </div>
              </div>
            </>
          )}

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={saving || uploading}>
              Abbrechen
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving || uploading}>
              {uploading ? 'Lädt hoch…' : saving ? 'Speichert…' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function getDefaultFormData(type) {
  if (type === 'media') {
    return {
      title: '',
      description: '',
      type: 'image',
      url: '',
      thumbnail_url: '',
      category: 'general',
      points_cost: 0,
      tags: '',
      is_nsfw: false,
      is_premium: false
    };
  } else if (type === 'collection') {
    return {
      name: '',
      description: '',
      cover_image: '',
      points_cost: 0,
      discount_percent: 0,
      is_premium: false
    };
  } else {
    return {
      name: '',
      display_name: '',
      description: '',
      icon: '📁',
      color: '#5865F2',
      sort_order: 0
    };
  }
}

export default Media;
